import clone from 'clone';
import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Activity, CustomFormAnswer, CustomFormInputField, CustomFormReportData, fieldValidation, FileUploadCustomFormFieldData, isInputField, rng, UserToken } from '../../../amorg-common';
import { deleteActivity, getActivity, updateActivity } from '../../../api/activityClientApi';
import { deleteCustomFormAnswer, deleteCustomFormReport, generateCustomFormTestAnswers, getCustomForm, getCustomFormAnswerList, getCustomFormByActivityId, getCustomFormReport, setCustomFormPublicLink, startCustomFormAnswer } from '../../../api/customFormClientApi';
import { getMaybeActiveUserOrRefresh, hasUserPermissions } from '../../../api/serverApiHelpers';
import { Button, ButtonProps } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { colors } from '../../../designSystem/colors/color';
import { DeleteConfirmationButton } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { LinkWithCopyButton } from '../../../designSystem/compositeComponents/LinkWithCopyButton';
import { FormField } from '../../../designSystem/formField/FormField';
import { Icon } from '../../../designSystem/icons/Icons';
import { StartEndDateFields } from '../../../designSystem/inputs/StartEndDateFields';
import { Label } from '../../../designSystem/Label';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P, Small } from '../../../designSystem/typography/Body';
import { clientAccessHelper } from '../../../utils/clientAccessHelper';
import { CustomFormFilter, customFormHelper } from '../../../utils/customFormHelper';
import { toDateTimeString } from '../../../utils/date';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { MemberPageWrapper } from '../../_pageWrappers/MemberPageWrapper';
import { customFormFieldFactory } from '../_components/customFormFieldFactory';
import { CustomFormTabHeader } from './_components/CustomFormTabHeader';
import { CustomFormFiltersPanel } from './report/_components/CustomFormFiltersPanel';
import { PageHeader } from '../../../designSystem/PageHeader';
import { ResponsiveColumns } from '../../../designSystem/ResponsiveColumns';
import { CustomForm } from '../../../amorg-common/DTOs/CustomForm';
import { LinkButton } from '../../../designSystem/button/LinkButton';
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';
import { IconWithText } from '../../../designSystem/compositeComponents/compositeIcons/IconWithText';


export function CustomFormPage (): JSX.Element
{
    const { activityId } = useParams<{ activityId: string; }>();
    const [selectedMenuItemKey, setSelectedMenuItemKey] = React.useState('');
    const [activityTitle, setActivityTitle] = React.useState('');

    React.useEffect(() =>
    {
        const load = async () =>
        {
            const form = await getCustomFormByActivityId(activityId || '');
            setSelectedMenuItemKey(form?.category || '');
        };
        load();
    }, [activityId]);

    return (
        <MemberPageWrapper
            selectedMenuItemKey={selectedMenuItemKey}
            breadCrumbs={[
                { text: 'Ergonomiske undersøgelser', url: '/ergonomics' },
                ...(activityTitle ? [{ text: activityTitle, url: '' }] : [])
            ]}>
            <CustomFormPageContent onActivityTitleChanged={setActivityTitle} />
        </MemberPageWrapper>
    );
}

type CustomFormPageContentProps = {
    onActivityTitleChanged: (title: string) => void,
};

function CustomFormPageContent (props: CustomFormPageContentProps): JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewCustomFormAnswerList']);

    const [redirectOverview, setRedirectOverview] = React.useState(false);
    const [redirectNewAnswerId, setRedirectNewAnswerId] = React.useState('');

    const { activityId } = useParams<{ activityId: string; }>();
    const navigate = useNavigate();

    const [state, setState] = React.useState<{
        isLoading: boolean,
        user: UserToken | null,
        form: CustomForm | null,
        activity: Activity | null,
        report: CustomFormReportData | null,
        answers: CustomFormAnswer[],
        filters: CustomFormFilter[] | null;
    }>(
        {
            isLoading: true, user: null, form: null, report: null, answers: [], filters: null,
            activity: {
                id: '',
                title: '',
                description: '',
                start: new Date(),
                end: new Date(),
                location: '',
                activityType: 'CustomForm',
                invitationType: 'InviteOnly',
                workgroupId: '',
                workgroupName: '',
                owner: null,
                participants: [],
                agendaItems: [],
            }
        });

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const user = (await getMaybeActiveUserOrRefresh()).tokenUser;
            const form = await getCustomFormByActivityId(activityId || '');
            const activity = await getActivity(form?.activityId || '');
            const answers = await getCustomFormAnswerList(form?.id || '');
            const report = await getCustomFormReport(form?.id || '');
            if (form && report)
            {
                const filters = customFormHelper.buildFilters(form, report);
                setState({ isLoading: false, user, form, activity, report, answers, filters });
            }
        };
        getData();
    }, []);

    async function onClickCreateNew ()
    {
        if (!state.form)
            return;

        setState({ ...state, isLoading: true });
        const newAnswerId = rng.getRandomId();
        await startCustomFormAnswer(state.form.id, newAnswerId);
        const answers = await getCustomFormAnswerList(state.form.id);
        setState({ ...state, isLoading: false, answers });
        setRedirectNewAnswerId(newAnswerId);
    }

    async function handleClickBuildTestAnswers ()
    {
        if (!state.form)
            return;

        setState({ ...state, isLoading: true });
        await generateCustomFormTestAnswers(state.form.id, 10);
        setState({
            ...state,
            answers: await getCustomFormAnswerList(state.form.id),
            isLoading: false
        });
    }

    function handleFilterChange (fieldId: string, value: string)
    {
        if (!state.filters)
            return;

        const newFilters = clone(state.filters);
        const filter = newFilters.find(f => f.fieldId === fieldId);

        if (filter)
        {
            filter.selectedValue = value;
        }

        setState({ ...state, filters: newFilters });
    }

    async function handleClickDeleteAnswer (answerId: string)
    {
        if (!state.form)
            return;

        setState({ ...state, isLoading: true });
        await deleteCustomFormAnswer(answerId);
        const answers = await getCustomFormAnswerList(state.form.id);
        const report = await getCustomFormReport(state.form.id);
        if (!report)
        {
            throw new Error('Unable to load report');
        }
        const filters = customFormHelper.buildFilters(state.form, report);
        setState({ ...state, isLoading: false, report, answers, filters });
    }

    async function handleLinkUpdated ()
    {
        if (!state.form)
            return;

        setState({ ...state, isLoading: true });
        const form = await getCustomForm(state.form.id);
        setState({ ...state, form });
    }

    async function handleSaveTitle (value: string, isValid: boolean)
    {
        if (!isValid || !state.activity)
            return;

        const a = state.activity;
        await updateActivity(a.id, value, a.description, a.start, a.end, a.location);
        const activity = await getActivity(state.activity.id);
        setState({ ...state, activity });
    }

    async function handleUpdateDates (start: Date, end: Date)
    {
        if (!state.activity)
            return;

        const a = state.activity;
        const result = await updateActivity(a.id, a.title, a.description, start, end, a.location);
        if (result.isSuccess)
        {
            const activity = await getActivity(state.activity.id);
            setState({ ...state, activity });
        }
    }

    let filteredAnswers: CustomFormAnswer[] = [];
    if (state.answers.length > 0 && state.filters && state.filters.length > 0)
    {
        filteredAnswers = customFormHelper.applyAnswerFilters(
            state.answers, state.filters
        );
    }

    const canEditLink = state.user ? hasUserPermissions(state.user, ['EditCustomForms']) : false;

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    React.useEffect(() =>
    {
        props.onActivityTitleChanged(state.activity?.title || '');
    }, [state.activity?.title]);

    return (

        <>
            <CustomFormTabHeader selectedTab='answers' answerCount={state.answers.length}
                onClick={(tabName) =>
                {
                    if (tabName === 'overview')
                        setRedirectOverview(true);
                }}
            />

            {state.form !== null && state.activity &&

                <>
                    <PageHeader
                        headerText='Ergonomisk undersøgelse'
                        buttons={
                            <>
                                <Button variant="text" icon="interface_actions_send" toolTipText="Opret 10 testbesvarelser"
                                    onClick={handleClickBuildTestAnswers}
                                />

                                <Button variant="text" icon="interface_actions_synchronize" toolTipText="Gendan rapport"
                                    onClick={async () =>
                                    {
                                        if (!state.form || !state.report)
                                            return;

                                        setState({ ...state, isLoading: true });
                                        await deleteCustomFormReport(state.form.id);
                                        setState({ ...state, isLoading: false });
                                    }}
                                />

                                <DeleteConfirmationButton
                                    variant='destructive-text'
                                    confirmationHeading='Slet undersøgelse'
                                    confirmationText='Er du sikker på at du vil slette undersøgelsen og alle dens besvarelser ?'
                                    requireKey
                                    onConfirmDelete={async () =>
                                    {
                                        if (!state.form)
                                            return;

                                        setState({ ...state, isLoading: true });
                                        await deleteActivity(state.activity?.id || '');
                                        navigate(`/ergonomics`);
                                    }}
                                />
                            </>
                        } 
                    />
                    <SingleColumnLayout>
                        <>
                            {redirectOverview &&
                                <Navigate to={`/customForms/${state.form.id}/report`} />
                            }

                            {redirectNewAnswerId &&
                                <Navigate to={`/customforms/${state.form.id}/answers/${redirectNewAnswerId}`} />
                            }

                            <LoadingPageOverlay loading={state.isLoading} />


                            <Card variant="outlined">
                                <Flex flexDirection='column' gap={16}>
                                    <Flex flexDirection='row' gap={16} justifyContent='space-between' alignItems='flex-end' flexWrap='wrap'>
                                        <div style={{ flex: '1 1 0', minWidth: '320px' }}>
                                            <EditableText
                                                styleMode='H2'
                                                ariaLabel={'Rediger undersøgelsens titel'}
                                                value={state.activity?.title || ''}
                                                validator={(value) =>
                                                {
                                                    if (!fieldValidation.activity.title(value))
                                                        return { valid: false, errorMessage: 'Feltet skal udfyldes. Maks. 100 tegn.' };
                                                    return { valid: true };
                                                }}
                                                onSave={handleSaveTitle} placeholder={''} />
                                        </div>

                                        <Flex flexDirection="row">
                                            <FormField
                                                label={"Periode"}
                                                inputComponent={
                                                    <StartEndDateFields
                                                        start={state.activity?.start || new Date()} end={state.activity?.end || new Date()}
                                                        onChanged={handleUpdateDates} />
                                                } />
                                        </Flex>
                                    </Flex>

                                    <FormLinkPanel form={state.form} canEdit={canEditLink} onUpdated={handleLinkUpdated} />
                                </Flex>
                            </Card>


                            <Flex flexDirection='row' gap={16} justifyContent='space-between' alignItems="flex-start">


                                {state.answers && state.report && state.filters &&

                                    <CustomFormFiltersPanel form={state.form} report={state.report} filter={state.filters}
                                        onFilterChange={handleFilterChange}
                                        buttons={[
                                            {
                                                variant: 'filled',
                                                text: "Opret besvarelse",
                                                icon: "add_circle",
                                                onClick: onClickCreateNew
                                            }]} />

                                }
                            </Flex>

                            {state.answers.length === 0 &&
                                <div>Ingen besvarelser oprettet</div>
                            }

                            {state.answers.length > 0 && filteredAnswers.length === 0 &&
                                <div>Ingen besvarelser med det valgte filter</div>
                            }

                            {state.form && state.answers &&
                                <>
                                    {filteredAnswers.map((answer) => 
                                    {
                                        return <AnswerCard key={answer.id} answer={answer} user={state.user} form={state.form as CustomForm} onDelete={() => handleClickDeleteAnswer(answer.id)} />
                                    }
                                    )}
                                </>
                            }
                        </>
                    </SingleColumnLayout>
                </>
            }

        </>
    );
}

type TimeLabelProps = {
    text: string;
};
function TimeLabel (props: TimeLabelProps): JSX.Element
{
    return (
        <Flex gap={4} alignItems='center'>
            <Icon name='clock' size='x-small' /><Small>{props.text}</Small>
        </Flex>
    );
}


type FormLinkPanelProps = {
    canEdit: boolean,
    form: CustomForm,
    onUpdated: () => void;
};
function FormLinkPanel (props: FormLinkPanelProps): JSX.Element
{
    const [siteId, setSiteId] = React.useState<string>('');

    React.useEffect(() =>
    {
        const getActiveUser = async () =>
        {
            const user = await getMaybeActiveUserOrRefresh([]);
            if (!user.tokenUser)
                return;

            setSiteId(user.tokenUser.siteId);
        };
        getActiveUser();

    }, []);

    async function handleCreateLink ()
    {
        await setCustomFormPublicLink(props.form.id, 'create');
        props.onUpdated();
    }

    async function handleDeleteLink ()
    {
        await setCustomFormPublicLink(props.form.id, 'remove');
        props.onUpdated();
    }

    const url = `${window.location.protocol}//${window.location.host}/customForms/public/${props.form?.publicLinkKey || '#'}@${siteId}`;

    return (
        <div>
            <Label>Link til oprettelse af besvarelser</Label>
            {!props.form?.publicLinkKey &&
                <Flex flexDirection="row" alignItems='center' gap={16}>
                    <div>Der er ikke oprettet et link til undersøgelsen</div>
                    {props.canEdit &&
                        <Button variant="outlined" text="Opret link" onClick={handleCreateLink} />
                    }
                </Flex>
            }

            {props.form?.publicLinkKey &&
                <LinkWithButtons
                    url={url}
                    disabled={!props.form?.publicLinkKey}
                    extraButtons={
                        props.canEdit
                            ? [{
                                icon: "interface_actions_delete",
                                variant: "destructive-text",
                                onClick: handleDeleteLink
                            }]
                            : []
                    }
                />
            }
        </div>
    );
}

type LinkWithButtonsProps = {
    url: string,
    disabled?: boolean,
    extraButtons?: ButtonProps[];
};
function LinkWithButtons (props: LinkWithButtonsProps): JSX.Element
{
    return (
        <Flex flexDirection="row" alignItems='center' gap={16}>
            <LinkWithCopyButton
                disabled={props.disabled}
                url={props.url}
                extraButtons={props.extraButtons} />
        </Flex>
    );
}

function AnswerCard({answer, form, user, onDelete}:{answer:CustomFormAnswer, form:CustomForm, user:UserToken | null, onDelete: () => void}):JSX.Element
{
    const inputFields = form?.fields.filter(f => isInputField(f));
    const publicUrl = `${window.location.protocol}//${window.location.host}/customforms/public/${user?.siteId}/${answer.id}`;
    const videoFields = form.fields.filter(f => isInputField(f) && f.type === 'FileUpload');
    const videoTypeSelections = answer.selections.filter(s => videoFields.find(f => f.fieldId === s.fieldId));
    const videoCount = videoTypeSelections.filter(s => 
    {
        if (!s.value) return false;
        try
        {
            const data = JSON.parse(s.value) as FileUploadCustomFormFieldData;
            return data.status.stage === 'complete';
        }
        catch (e)
        {
            return false;
        }
    }).length;
    return (
        <Card key={answer.id} variant="outlined">

            <Flex flexDirection='column' gap={16}>
                {inputFields &&
                    <CardAnswerFields form={form} answer={answer} inputFields={inputFields} />
                }


                <Flex justifyContent='space-between' flexWrap='wrap'>

                        <LinkWithCopyButton url={publicUrl} />

                    <Flex justifyContent='flex-end' alignItems='center' gap={16}>
                        <IconWithText icon="smart_display" variant={videoCount === 0 ? 'disabled' : 'active'}>{videoCount.toString()}</IconWithText>
                        <TimeLabel text={`oprettet ${toDateTimeString(answer.started)}`} />
                        <TimeLabel text={`sidst redigeret ${toDateTimeString(answer.lastEdited)}`} />
                    </Flex>

                </Flex>

                <Flex gap={16} justifyContent='flex-end'>
                    <LinkButton href={`/customForms/${form.id}/answers/${answer.id}`}
                        variant='light'
                        text='Rediger'
                        icon='interface_actions_edit'/>
                    <DeleteConfirmationButton buttonText='Slet besvarelsen' confirmationHeading='Slet besvarelse' requireKey={false}
                        confirmationText='Er du sikker på at du vil slette besvarelsen?'
                        onConfirmDelete={onDelete} />
                </Flex>


            </Flex>
        </Card>
    );
}

function CardAnswerFields(props:{
    form: CustomForm,
    answer: CustomFormAnswer,
    inputFields: CustomFormInputField[],
}):JSX.Element
{
    if(props.form.templateId.includes('kim')) {
        return <KimCardAnswerFields form={props.form} inputFields={props.inputFields} answer={props.answer} />;
    }

    return (
        <ResponsiveColumns columnCount={4} tabletColumnCount={2} mobileColumnCount={1} style={{ rowGap: 8, columnGap: 8 }}>
            {props.inputFields.filter(f => f.isPrimaryField).map((field: CustomFormInputField) =>
            {
                const answerSelection = props.answer.selections.find(s => s.fieldId === field.fieldId);

                return (
                    <Card variant="outlined" key={field.fieldId}>
                        <Flex flexDirection='column' alignItems='flex-start'>
                            <Small>{field.label}</Small>
                            <P>{answerSelection
                                ? customFormFieldFactory.getAnswerFieldText(field, answerSelection)
                                : ''}
                            </P>
                        </Flex>
                    </Card>
                );
            })}
        </ResponsiveColumns>
    );
}

function KimCardAnswerFields(props:{
    form: CustomForm,
    inputFields: CustomFormInputField[],
    answer: CustomFormAnswer,
}):JSX.Element
{

    function getFieldValue(fieldId: string): { label:string, value:string } | null {
        const field = props.inputFields.find(f => f.fieldId === fieldId);
        const selection = field ? props.answer.selections.find(s => s.fieldId === field.fieldId) : undefined;
        const value = field && selection ? customFormFieldFactory.getAnswerFieldText(field, selection) : '';
        return (field && selection && value)
            ?   {
                    label: field.label,
                    value
                }
            : null
    }

    const activityFieldValue = getFieldValue('activity');
    const departmentFieldValue = getFieldValue('department');
    const assessedByFieldValue = getFieldValue('assessedBy');
    const riskFieldValue = getFieldValue('risk');
    const numericRiskFieldValue = riskFieldValue && !isNaN(parseFloat(riskFieldValue.value))
        ? { label: riskFieldValue.label, value: parseFloat(riskFieldValue.value) }
        : null;

    return (
        <ResponsiveColumns columnCount={4} tabletColumnCount={2} mobileColumnCount={1} style={{ rowGap: 24, columnGap: 8 }}>
            {activityFieldValue &&
                <FormField label={activityFieldValue.label} inputComponent={
                    <span>{activityFieldValue.value}</span>
                } />
            }
            {departmentFieldValue &&
                <FormField label={departmentFieldValue.label} inputComponent={
                    <span>{departmentFieldValue.value}</span>
                } />
            }
            {assessedByFieldValue &&
                <FormField label={assessedByFieldValue.label} inputComponent={
                    <span>{assessedByFieldValue.value}</span>
                } />
            }
            {numericRiskFieldValue &&
                <FormField label={numericRiskFieldValue.label} 
                    inputComponent={
                        <>
                            { numericRiskFieldValue.value > 50 &&
                                <TextAvatar size='32px' text={ numericRiskFieldValue.value.toString() } backgroundColor={colors.icon.error} />
                            }

                            { numericRiskFieldValue.value <= 50 && numericRiskFieldValue.value > 25 &&
                                <TextAvatar size='32px' text={ numericRiskFieldValue.value.toString() } backgroundColor={colors.icon.warning} />
                            }

                            { numericRiskFieldValue.value <= 25 && numericRiskFieldValue.value > 10 &&
                                <TextAvatar size='32px' text={ numericRiskFieldValue.value.toString() } backgroundColor={colors.icon.passive} />
                            }

                            { numericRiskFieldValue.value < 10 &&
                                <TextAvatar size='32px' text={ numericRiskFieldValue.value.toString() } backgroundColor={colors.icon.success} />
                            }

                        </>
                } />
            }
        </ResponsiveColumns>
    );
}